import type { ApplicableContext } from '@cotera/client/v0/actions/framework';
import { isDraftOnlyTriggerInWorkflow } from './workflow-draft-graph';
import {
  targetedCronTriggerNodeModel,
  targetedEventTriggerNodeModel,
  targetedWebhookTriggerNodeModel,
  workflowModelFromContext,
} from './workflow-toolbar-action.helpers';
import type { WorkflowModel } from './workflow.model';

/**
 * Draft test runs are required when the editor graph is unsaved, the workflow
 * is still new, or the targeted trigger only exists in local draft state.
 */
export function shouldUseDraftTestRun(context: ApplicableContext): boolean {
  const model = workflowModelFromContext(context);
  if (model === null) {
    return false;
  }
  if (model.isNew === true) {
    return true;
  }
  if (model.hasUnsavedChangesWatchable.snapshot() === true) {
    return true;
  }

  const webhook = targetedWebhookTriggerNodeModel(context);
  if (webhook !== null) {
    return isDraftOnlyTriggerInWorkflow(
      model.getDraftState(),
      webhook.triggerId,
      model.isNew
    );
  }

  const cron = targetedCronTriggerNodeModel(context);
  if (cron !== null) {
    return isDraftOnlyTriggerInWorkflow(
      model.getDraftState(),
      cron.triggerId,
      model.isNew
    );
  }

  const event = targetedEventTriggerNodeModel(context);
  if (event !== null) {
    return isDraftOnlyTriggerInWorkflow(
      model.getDraftState(),
      event.triggerId,
      model.isNew
    );
  }

  return false;
}

export async function startDraftTestRunFromModel(
  model: WorkflowModel
): Promise<
  | { ok: true; runId: string; ephemeralWorkflowId: string }
  | { ok: false; message: string }
> {
  const result = await model.startDraftTestRun();
  if (result.success === false) {
    return { ok: false, message: result.message };
  }
  return {
    ok: true,
    runId: result.runId,
    ephemeralWorkflowId: result.ephemeralWorkflowId,
  };
}
